import { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import syncService from '../services/syncService';
import apiClient from '../services/apiClient';
import { resolveDarkMode } from '../utils/theme';

/**
 * The one place editor/AI preferences live on the web
 * (openspec/changes/unify-user-preferences, Phase 1).
 *
 * Every consumer -- ChatSidebar, CreationChatInput, AskInput, PreferencesPanel,
 * useMargins, ... -- reads this single state through `useEditorPreferences()`.
 * There is no per-component copy and no `preferenceChanged` window event: a
 * change here re-renders every consumer through normal context propagation.
 *
 * Persistence: `localStorage.screenwriter_preferences` is the first-paint cache;
 * the server copy is `user_preferences` (type `editor`) via /api/preferences.
 */

export const STORAGE_KEY = 'screenwriter_preferences';

export const DEFAULT_PREFERENCES = {
  // Visual settings
  hoverColor: '#f5f5f5',
  highlightColor: '#FFFF00',
  fontSize: 16,
  // Theme. Left undefined until the user picks one, so a new user follows the
  // operating system; once set it follows the user across devices.
  isDarkMode: undefined,

  // Interaction
  stickyHover: true,
  performanceMode: true,
  lineHeight: 1.4,

  // Language -- independent axes. `contentLanguage` is the ONE "document
  // language": the language a new file starts in AND the language of AI-generated
  // document prose (it replaced the separate `defaultFileLanguage`). It is left
  // undefined until the user picks one, so "never chosen" is distinguishable from
  // English; consumers fall back with `|| 'en'`.

  // Axis 1: Dictation -- what language you're speaking, for STT accuracy only. No phonetic/native mode.
  dictationLanguage: 'en',

  // Axis 2: AI reply/planning language -- the AI's own conversational reply, planning-step text
  // (scene/chapter beats, plan-confirmation prompt, clarify questions), and Notes/Outlines generation.
  aiReplyLanguage: 'en',

  // Axis 3: Document language + its phonetic/native mode toggle.
  contentLanguage: undefined,
  contentLanguageMode: 'phonetic', // 'phonetic' (e.g. Tanglish) or 'native' (e.g. Tamil script)

  // Transliteration
  enableTransliteration: false,
  transliterationLanguage: 'ta',
  autoTransliterate: false,

  // Document margins
  margins: {
    top: 1.0,
    bottom: 1.0,
    left: 1.5,
    right: 1.0,
    unit: 'in',
    showMarginGuides: true
  },

  // Viewport settings
  zoomLevel: 100,
  isPageView: true, // Page view as default
  isFooterVisible: true,
  pageSize: 'us-letter',
  showPageNumbers: true,
  sceneNumbering: false,
  sceneNumberingPosition: 'left', // 'left' | 'right' | 'both'

  // Spoken-word rate used by the shortform-family beat timing plugin
  // (Instagram/YouTube/Podcast) to estimate cumulative timecodes from word
  // count -- see beatTimingPlugin.js. Not used by any other template.
  shortformWordsPerMinute: 150,

  // Auto edit mode: when true (default), AI edits apply directly to the editor
  // without review. When false, apply_patch returns a pending proposal instead of
  // writing (openspec/changes/redesign-artifact-card-panel). Must default true:
  // a silent user who never touched the toggle must see zero behavior change.
  autoEditMode: true,

  // Private per-user guidance sent to Auto requests. Never logged or stored
  // in chat/session metadata; persistence uses the existing editor preferences API.
  systemInstructions: '',

  // AI Agent settings
  agentSettings: {
    use_agent: true,  // Always use agent by default
    temperature: 0.7,
    frequency_penalty: 0.0,
    presence_penalty: 0.0,
    enable_streaming: true  // Always stream to show agentic thinking process
  }
};

// Re-fetching on window focus is what lets a change made on another device show
// up here without a reload; throttled so tab-flipping doesn't spam the API.
const FOCUS_REFETCH_MIN_MS = 30000;

export const DIRTY_KEY = 'screenwriter_preferences_dirty';

const readJSON = (key) => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw || raw === 'undefined' || raw === 'null') return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

/**
 * `defaultFileLanguage` and `contentLanguage` are one preference now. A user
 * who only ever set the old key keeps their language; if both exist and differ,
 * `contentLanguage` wins (it is what the AI actually generates in today).
 * Returns the migrated copy and whether a stale key was dropped.
 */
export const mergeDefaultFileLanguage = (prefs) => {
  if (!prefs || prefs.defaultFileLanguage === undefined) return { prefs, dropped: false };
  const { defaultFileLanguage, ...rest } = prefs;
  if (rest.contentLanguage === undefined && defaultFileLanguage) {
    rest.contentLanguage = defaultFileLanguage;
  }
  return { prefs: rest, dropped: true };
};

/**
 * One-time upgrade of older stored shapes into the current one:
 *  - chatLanguage/chatGenerationMode -> aiReplyLanguage + contentLanguage(+Mode)
 *  - standalone documentMargins/marginUnit/showMarginGuides -> margins
 *  - standalone darkMode -> isDarkMode
 *  - defaultFileLanguage -> contentLanguage
 *  - isPageView is forced true (page view is the default for everyone)
 * The legacy localStorage keys are removed once folded in.
 */
export const migrateStoredPreferences = (stored, { removeLegacy = true } = {}) => {
  let migrated = { ...stored, isPageView: true };

  // Theme used to live in its own `darkMode` key (and, separately, in the
  // UIState and App state). Adopt it once, as the account preference.
  if (migrated.isDarkMode === undefined) {
    const legacyTheme = readJSON('darkMode');
    if (typeof legacyTheme === 'boolean') migrated.isDarkMode = legacyTheme;
  }

  if (migrated.chatLanguage && !migrated.aiReplyLanguage && !migrated.contentLanguage) {
    migrated = {
      ...migrated,
      aiReplyLanguage: migrated.chatLanguage,
      contentLanguage: migrated.chatLanguage,
      contentLanguageMode: migrated.chatGenerationMode || 'phonetic',
    };
  }

  if (!migrated.margins) {
    const legacyMargins = readJSON('documentMargins');
    const legacyUnit = localStorage.getItem('marginUnit');
    const legacyGuides = localStorage.getItem('showMarginGuides');
    if (legacyMargins || legacyUnit || legacyGuides !== null) {
      migrated.margins = {
        ...DEFAULT_PREFERENCES.margins,
        ...(legacyMargins || {}),
        ...(legacyUnit ? { unit: legacyUnit } : {}),
        ...(legacyGuides !== null ? { showMarginGuides: legacyGuides === 'true' } : {}),
      };
    }
  }
  if (removeLegacy) {
    ['documentMargins', 'marginUnit', 'showMarginGuides', 'darkMode'].forEach((key) => {
      try { localStorage.removeItem(key); } catch { /* cache only */ }
    });
  }

  return mergeDefaultFileLanguage(migrated).prefs;
};

// First render already has the cached preferences (no default-theme flash). Pure:
// legacy keys are only read here; the load effect removes them once persisted.
const readInitialPreferences = () => {
  const stored = readJSON(STORAGE_KEY);
  const legacyTheme = readJSON('darkMode');
  if (!stored && typeof legacyTheme !== 'boolean') return DEFAULT_PREFERENCES;
  return { ...DEFAULT_PREFERENCES, ...migrateStoredPreferences(stored || {}, { removeLegacy: false }) };
};

const PreferencesStateContext = createContext(null);

export const usePreferencesState = () => {
  const context = useContext(PreferencesStateContext);
  if (!context) {
    throw new Error('useEditorPreferences must be used within PreferencesStateProvider');
  }
  return context;
};

export const PreferencesStateProvider = ({ children }) => {
  const [preferences, setPreferences] = useState(readInitialPreferences);
  const [isLoaded, setIsLoaded] = useState(false);
  // Mirrors `preferences` synchronously (state only updates on the next render).
  // updatePreference merges against this so rapid successive calls never lose a
  // key, and exposed so callers that must read the latest value inside a
  // callback (useChatMessages) don't wait for a render.
  const preferencesRef = useRef(preferences);
  // Keys changed locally whose push to the server hasn't succeeded yet -- a
  // server refetch must not overwrite these. Persisted so an offline edit
  // survives a reload and is still pushed (not clobbered) next time.
  const dirtyKeysRef = useRef(new Set(readJSON(DIRTY_KEY) || []));
  const lastHydrateRef = useRef(0);

  const persist = useCallback((prefs) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
    } catch (error) {
      console.error('Error saving preferences to localStorage:', error);
    }
  }, []);

  const persistDirty = useCallback(() => {
    try {
      localStorage.setItem(DIRTY_KEY, JSON.stringify([...dirtyKeysRef.current]));
    } catch { /* cache only */ }
  }, []);

  // Push every unsynced key (PATCH, changed keys only). A key is cleared only if
  // its value is unchanged since the snapshot, so an edit made while the request
  // was in flight stays dirty.
  const flushDirty = useCallback(async () => {
    const keys = [...dirtyKeysRef.current];
    if (keys.length === 0) return;
    const status = syncService.getSyncStatus();
    if (!status.authToken || !status.isOnline) return;
    const snapshot = Object.fromEntries(keys.map((key) => [key, JSON.stringify(preferencesRef.current[key])]));
    try {
      const ok = await syncService.syncPreferences(keys);
      if (!ok) return;
      keys.forEach((key) => {
        if (JSON.stringify(preferencesRef.current[key]) === snapshot[key]) dirtyKeysRef.current.delete(key);
      });
      persistDirty();
    } catch (error) {
      console.warn('Failed to sync preference to server:', error);
    }
  }, [persistDirty]);

  const hydrateFromServer = useCallback(async () => {
    if (!localStorage.getItem('currentUser') || !syncService.getSyncStatus().isOnline) return;
    lastHydrateRef.current = Date.now();
    try {
      // apiClient's default export is the helper that already unwraps
      // `response.data`; reading `.status`/`.data` off it (as the old hook did)
      // silently skipped this whole branch.
      const data = await apiClient.get('/api/preferences');
      const { prefs: serverEditor, dropped } = mergeDefaultFileLanguage(data?.preferences?.editor || null);
      if (dropped) {
        // The server still holds the retired key; remove it so it can't come back.
        apiClient.delete('/api/preferences/editor/defaultFileLanguage').catch(() => {});
      }
      if (!serverEditor) return;

      const merged = { ...preferencesRef.current };
      let changed = false;
      for (const [key, value] of Object.entries(serverEditor)) {
        if (dirtyKeysRef.current.has(key)) continue;
        if (JSON.stringify(merged[key]) !== JSON.stringify(value)) {
          merged[key] = value;
          changed = true;
        }
      }
      if (changed) {
        preferencesRef.current = merged;
        setPreferences(merged);
        persist(merged);
      }
    } catch (error) {
      console.warn('Failed to load preferences from server:', error);
    }
  }, [persist]);

  // Initial load: local cache first (instant), then the server.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const stored = readJSON(STORAGE_KEY);
        const hadLegacyTheme = typeof readJSON('darkMode') === 'boolean';
        if (stored || hadLegacyTheme) {
          const migrated = migrateStoredPreferences(stored || {});
          const next = { ...DEFAULT_PREFERENCES, ...migrated };
          preferencesRef.current = next;
          setPreferences(next);
          persist(migrated);
          // A theme adopted from the legacy key has never reached the server.
          if (hadLegacyTheme && stored?.isDarkMode === undefined) {
            dirtyKeysRef.current.add('isDarkMode');
            persistDirty();
          }
        }
        await flushDirty();
        await hydrateFromServer();
      } catch (error) {
        console.error('Error loading preferences:', error);
      } finally {
        if (!cancelled) setIsLoaded(true);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [flushDirty, hydrateFromServer, persist, persistDirty]);

  // Pick up changes made on another device when the user returns to this tab.
  useEffect(() => {
    const onFocus = () => {
      if (Date.now() - lastHydrateRef.current >= FOCUS_REFETCH_MIN_MS) hydrateFromServer();
    };
    // Coming back online: push what was edited offline, then refetch.
    const onOnline = async () => {
      await flushDirty();
      hydrateFromServer();
    };
    window.addEventListener('focus', onFocus);
    window.addEventListener('online', onOnline);
    return () => {
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('online', onOnline);
    };
  }, [flushDirty, hydrateFromServer]);

  // Apply CSS custom properties when preferences change
  useEffect(() => {
    if (!isLoaded) return;

    const root = document.documentElement;
    root.style.setProperty('--editor-font-size', `${preferences.fontSize}px`);
    root.style.setProperty('--hover-color', preferences.hoverColor);
    root.style.setProperty('--highlight-color', preferences.highlightColor);
    const dark = resolveDarkMode(preferences.isDarkMode);
    root.classList.toggle('dark-mode', dark);
    if (dark) {
      root.setAttribute('data-color-scheme', 'dark');
    } else {
      root.removeAttribute('data-color-scheme');
    }
  }, [preferences, isLoaded]);

  const updatePreference = useCallback((key, value) => {
    if (key === 'systemInstructions') value = String(value || '').slice(0, 12000);
    const updated = { ...preferencesRef.current, [key]: value };
    preferencesRef.current = updated;
    dirtyKeysRef.current.add(key);
    persistDirty();
    setPreferences(updated);
    persist(updated);
    flushDirty();
  }, [persist, persistDirty, flushDirty]);

  const resetPreferences = useCallback(async () => {
    const keys = Object.keys(DEFAULT_PREFERENCES);
    preferencesRef.current = DEFAULT_PREFERENCES;
    keys.forEach((key) => dirtyKeysRef.current.add(key));
    persistDirty();
    setPreferences(DEFAULT_PREFERENCES);
    persist(DEFAULT_PREFERENCES);
    flushDirty();
  }, [persist, persistDirty, flushDirty]);

  const value = useMemo(() => ({
    preferences,
    isLoaded,
    updatePreference,
    resetPreferences,
    preferencesRef,
  }), [preferences, isLoaded, updatePreference, resetPreferences]);

  return (
    <PreferencesStateContext.Provider value={value}>
      {children}
    </PreferencesStateContext.Provider>
  );
};
